/**
 * Mock 시뮬레이터 (프롬프트 §9).
 * - 1~2초마다 일부 노드만(3~8개) 처리량·latency 를 흔든다.
 * - 가끔 한 노드에 장애 시퀀스를 건다: error → (재시도 이벤트) → recovering → 복구 완료(healthy/running).
 * - 이벤트 로그는 최대 500건 유지.
 * - 브라우저에서만 동작 (setTimeout). paused 면 tick 을 건너뛴다.
 */
import { createMockSnapshot } from "../data/mock-pipeline";
import { computeSummary, createEdges, deriveEdges, round1 } from "./pipeline-graph";
export const SIMULATOR_MAX_EVENTS = 500;
export class PipelineSimulator {
    constructor(opts = {}) {
        this.baseEdges = createEdges();
        this.incidents = new Map();
        this.tick = 0;
        this.timer = null;
        this.paused = false;
        this.listeners = new Set();
        this.seq = 0;
        this.mode = opts.mode ?? "mock";
        this.minMs = opts.minIntervalMs ?? 1000;
        this.maxMs = opts.maxIntervalMs ?? 2000;
        this.incidentP = opts.incidentProbability ?? 0.12;
        const initial = opts.initial ?? createMockSnapshot({ mode: this.mode, seed: opts.seed });
        // 깊은 복사로 외부 스냅샷과 분리
        this.nodes = initial.nodes.map((n) => ({ ...n, data: { ...n.data, extra: n.data.extra ? { ...n.data.extra } : undefined } }));
        this.events = initial.events.slice(0, SIMULATOR_MAX_EVENTS);
    }
    /** 현재 상태로 스냅샷 생성 (mode 는 생성자 옵션 그대로) */
    getSnapshot() {
        const nodes = this.nodes;
        return {
            mode: this.mode,
            generatedAt: new Date().toISOString(),
            nodes,
            edges: deriveEdges(nodes, this.baseEdges),
            events: this.events,
            summary: computeSummary(nodes),
        };
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    start() {
        if (this.timer)
            return;
        this.schedule();
    }
    stop() {
        if (this.timer)
            clearTimeout(this.timer);
        this.timer = null;
    }
    setPaused(paused) {
        this.paused = paused;
    }
    isRunning() {
        return this.timer !== null;
    }
    /** 테스트·수동 진행용: 한 tick 강제 실행 */
    step() {
        this.advance();
        const snap = this.getSnapshot();
        this.listeners.forEach((l) => l(snap));
        return snap;
    }
    schedule() {
        const delay = this.minMs + Math.random() * (this.maxMs - this.minMs);
        this.timer = setTimeout(() => {
            this.timer = null;
            if (!this.paused)
                this.step();
            this.schedule();
        }, delay);
    }
    // ---------------- 내부 로직 ----------------
    pushEvent(level, message, nodeId) {
        this.seq += 1;
        const ev = {
            id: `sim:${Date.now()}:${this.seq}`,
            timestamp: new Date().toISOString(),
            level,
            nodeId,
            message,
        };
        this.events = [ev, ...this.events].slice(0, SIMULATOR_MAX_EVENTS);
    }
    replaceNode(idx, patch) {
        const n = this.nodes[idx];
        const next = { ...n, data: { ...n.data, ...patch, lastUpdated: new Date().toISOString() } };
        // 변경된 노드만 새 객체로 — store diff 가 나머지를 그대로 유지하도록 배열도 복사
        const arr = this.nodes.slice();
        arr[idx] = next;
        this.nodes = arr;
    }
    advance() {
        this.tick += 1;
        const total = this.nodes.length;
        // 1) 일부 노드만 값 흔들기
        const count = 3 + Math.floor(Math.random() * 6);
        const picked = new Set();
        while (picked.size < Math.min(count, total))
            picked.add(Math.floor(Math.random() * total));
        for (const idx of picked) {
            const n = this.nodes[idx];
            if (this.incidents.has(n.id))
                continue;
            const d = n.data;
            if (d.status === "offline" || d.status === "error")
                continue;
            const jitter = 1 + (Math.random() - 0.5) * 0.3;
            const throughput = round1(Math.max(0, d.throughput * jitter + (d.throughput === 0 ? Math.random() * 0.4 : 0)));
            const latency = d.nodeType === "source"
                ? Math.max(0, Math.round(d.latency + (Math.random() - 0.6) * 20))
                : Math.max(0, Math.round(d.latency + (Math.random() - 0.5) * 6));
            const status = d.status === "warning" || d.status === "recovering"
                ? d.status
                : throughput > 0
                    ? "running"
                    : "healthy";
            this.replaceNode(idx, { throughput, latency, status });
        }
        // 2) 진행 중 장애 시퀀스 진행
        for (const inc of [...this.incidents.values()]) {
            if (this.tick < inc.untilTick)
                continue;
            const idx = this.nodes.findIndex((n) => n.id === inc.nodeId);
            if (idx < 0) {
                this.incidents.delete(inc.nodeId);
                continue;
            }
            const label = this.nodes[idx].data.label;
            if (inc.phase === "error") {
                inc.phase = "retrying";
                inc.retries += 1;
                inc.untilTick = this.tick + 1 + Math.floor(Math.random() * 2);
                this.pushEvent("warning", `${label} 재시도 ${inc.retries}회`, inc.nodeId);
                this.replaceNode(idx, { retryCount: inc.retries });
            }
            else if (inc.phase === "retrying") {
                if (inc.retries < 2 && Math.random() < 0.4) {
                    inc.retries += 1;
                    inc.untilTick = this.tick + 1 + Math.floor(Math.random() * 2);
                    this.pushEvent("warning", `${label} 재시도 ${inc.retries}회`, inc.nodeId);
                    this.replaceNode(idx, { retryCount: inc.retries });
                }
                else {
                    inc.phase = "recovering";
                    inc.untilTick = this.tick + 2 + Math.floor(Math.random() * 3);
                    this.pushEvent("info", `${label} 복구 중 — 재연결 성공, 밀린 데이터 전송`, inc.nodeId);
                    this.replaceNode(idx, {
                        status: "recovering",
                        description: "복구 중 (밀린 데이터 전송)",
                        throughput: round1(inc.prevThroughput * 0.5),
                    });
                }
            }
            else {
                this.incidents.delete(inc.nodeId);
                this.pushEvent("success", `${label} 복구 완료`, inc.nodeId);
                this.replaceNode(idx, {
                    status: inc.prevStatus === "error" || inc.prevStatus === "offline" ? "running" : inc.prevStatus,
                    description: inc.prevDescription,
                    throughput: inc.prevThroughput,
                    successRate: 100,
                    retryCount: 0,
                });
            }
        }
        // 3) 새 장애 시작 (동시 최대 3건)
        if (this.incidents.size < 3 && Math.random() < this.incidentP) {
            const candidates = this.nodes
                .map((n, i) => ({ n, i }))
                .filter(({ n }) => !this.incidents.has(n.id) &&
                n.data.status !== "offline" &&
                n.data.status !== "error" &&
                n.data.nodeType !== "output");
            if (candidates.length > 0) {
                const { n, i } = candidates[Math.floor(Math.random() * candidates.length)];
                const reason = pickReason(n);
                this.incidents.set(n.id, {
                    nodeId: n.id,
                    phase: "error",
                    untilTick: this.tick + 1 + Math.floor(Math.random() * 2),
                    retries: 0,
                    prevStatus: n.data.status,
                    prevThroughput: n.data.throughput,
                    prevDescription: n.data.description,
                });
                this.pushEvent("error", `${n.data.label} 오류 — ${reason}`, n.id);
                this.replaceNode(i, {
                    status: "error",
                    description: reason,
                    throughput: 0,
                    successRate: round1(60 + Math.random() * 30),
                });
            }
        }
    }
}
function pickReason(n) {
    switch (n.data.nodeType) {
        case "source":
            return "OPC UA 세션 끊김 (BadSessionIdInvalid)";
        case "processor":
            return n.id.startsWith("uploader")
                ? "MariaDB 연결 실패 (ECONNREFUSED)"
                : n.id.startsWith("outbox")
                    ? "SQLite 잠금 대기 초과 (SQLITE_BUSY)"
                    : "구독 오버플로 — PublishingInterval 초과";
        case "storage":
            return "잠금 대기 초과 (Lock wait timeout)";
        default:
            return "일시 오류";
    }
}
